<template>
  <div class="async-task-module"
       v-loading="committing"
       element-loading-text="启动任务中，请稍等...">
    <slot
      :start-task="startTask"
      :executing="executing"
      :complete="complete"
      :result="result"
      :state-info="stateInfo"
      :state-data="stateData">
      <div class="async-task-start">
        <div class="confirm-msg">
          <span>{{ confirmMsg }}</span>
        </div>
        <div class="btn-group">
          <el-button size="mini"
                     type="primary"
                     :loading="executing"
                     @click="startTask">{{executing ? '执行中...' : '确认执行'}}</el-button>
        </div>
      </div>
    </slot>
    <slot name="state"
          :start-task="startTask"
          :executing="executing"
          :complete="complete"
          :result="result"
          :state-info="stateInfo"
          :state-data="stateData">
      <div class="async-task-state" v-if="executing">
        <div class="state-content">
          <div class="progress-wrap">
            <el-progress :stroke-width="12"
                         :percentage="progress"></el-progress>
          </div>
          <div class="state-message" v-if="stateMessage">
            <span>{{ stateMessage }}</span>
          </div>
        </div>
      </div>
    </slot>
    <slot name="complete"
          :result="result"
          :close="close">
      <transition name="el-zoom-in-bottom">
        <div class="async-task-complete" v-if="complete">
          <div class="complete-content">
            <div class="complete-icon">
              <i class="el-icon-success"></i>
            </div>
            <div class="complete-title">
              <span>{{ resultMessage }}</span>
            </div>
            <div class="complete-actions">
              <template v-if="result.download && result.download.url">
                <div class="result-action">
                  <div class="result-action-back">
                    <el-button size="small" circle icon="el-icon-arrow-left" @click="close"></el-button>
                  </div>
                  <el-link :href="$contextPath(result.download.url)"
                           type="primary"
                           target="_blank"
                           :underline="false">
                    <i class="el-icon-download"></i>
                    <span>{{ result.download.name || '点击下载文件' }}</span>
                  </el-link>
                </div>
              </template>
              <template v-else>
                <div class="result-close">
                  <el-button type="primary" size="small" plain round @click="close">确定</el-button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </transition>
    </slot>
  </div>
</template>

<script>
export default {
  name: 'AsyncTaskModule',
  props: {
    actionUrl: {
      type: String,
      required: true
    },
    confirmMsg: {
      type: String,
      default: '确定执行此任务吗？'
    },
    preHandleParams: {
      type: Function
    },
    extraParams: {
      type: Object,
      required: false
    },
    preHandleVerify: {
      type: Function
    },
    pollInterval: {
      type: Number,
      default: 1000
    },
    completeMessage: {
      type: String,
      default: '任务已执行完毕'
    }
  },
  data () {
    return {
      committing: false,
      executing: false,
      complete: false,
      stateInfo: null,
      stateData: null,
      result: null,
      pollTimer: null
    }
  },
  computed: {
    progress () {
      if (this.stateInfo && typeof this.stateInfo.percent === 'number') {
        return this.stateInfo.percent
      }
      return 0
    },
    stateMessage () {
      if (this.stateInfo && this.stateInfo.message) {
        return this.stateInfo.message
      }
      return '任务执行中...'
    },
    resultMessage () {
      if (this.result) {
        if (typeof this.result === 'string') {
          return this.result
        }
        if (this.result.message) {
          return this.result.message
        }
      }
      return this.completeMessage
    }
  },
  methods: {
    getActionUrl (action) {
      const actionUrl = this.actionUrl
      const quotIndex = actionUrl.indexOf('?')
      if (quotIndex > 0) {
        return `${actionUrl.substring(0, quotIndex)}${action}${actionUrl.substring(quotIndex)}`
      } else {
        return `${actionUrl}${action}`
      }
    },
    getActionParams () {
      if (this.preHandleParams) {
        return this.$extend(this.preHandleParams(), this.extraParams)
      } else {
        return this.extraParams || {}
      }
    },
    startTask () {
      if (this.preHandleVerify && !this.preHandleVerify()) {
        return
      }
      this.executing = false
      this.complete = false
      this.stateInfo = null
      this.stateData = null
      this.result = null
      this.committing = true
      const params = this.$extend(this.getActionParams(), this.$attrs)
      this.$ajax({
        url: this.getActionUrl('Start'),
        method: 'post',
        data: this.$qs.stringify(params, {
          arrayFormat: 'repeat'
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.executing = true
              this.schedulePoll()
              break
            }
            default: {
              this.$message.warning(response.data['message'] || '启动任务失败')
              break
            }
          }
        }
      }, () => {
        this.$message.error('请求发送失败,请稍后重试')
      }).finally(() => {
        this.committing = false
      })
    },
    pollStatus () {
      if (!this.executing) {
        return
      }
      this.$ajax({
        url: this.getActionUrl('State'),
        method: 'post'
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              if (response.data.complete) {
                this.handleComplete(response.data)
              } else {
                this.stateInfo = response.data['stateInfo'] || {}
                this.stateData = response.data['stateData']
                this.schedulePoll()
              }
              break
            }
            default: {
              this.$message.warning(response.data['message'] || '获取任务状态失败')
              this.handleError()
              break
            }
          }
        }
      }, () => {
        this.schedulePoll()
      })
    },
    schedulePoll () {
      if (this.executing) {
        this.pollTimer = setTimeout(() => {
          this.pollStatus()
        }, this.pollInterval)
      }
    },
    stopPolling () {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    },
    handleComplete (data) {
      this.executing = false
      this.complete = true
      this.result = data.result
      this.stopPolling()
      this.$emit('onComplete')
    },
    handleError () {
      this.executing = false
      this.stopPolling()
      this.$emit('onError')
    },
    close () {
      this.complete = false
      this.executing = false
      this.stateInfo = null
      this.stateData = null
      this.result = null
      this.$emit('onClose')
    }
  },
  beforeDestroy () {
    this.stopPolling()
  }
}
</script>

<style lang="scss">
.async-task-module {
  position: relative;
  height: 100%;
  min-height: 180px;
  overflow: hidden;

  .async-task-start {
    padding: 20px 15px;
    text-align: center;

    .confirm-msg {
      padding: 20px 0;
      color: #606266;
      font-size: 14px;
    }

    .btn-group {
      margin-top: 15px;
    }
  }

  .async-task-state {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 100;

    .state-content {
      width: 260px;
      max-width: 90%;

      .progress-wrap {
        .el-progress {
          .el-progress-bar {
            margin-right: -45px;
          }

          .el-progress__text {
            margin-left: 10px;
            font-size: 12px !important;
          }
        }
      }

      .state-message {
        margin-top: 10px;
        color: #909399;
        font-size: 12px;
        text-align: left;
      }
    }
  }

  .async-task-complete {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.95);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 101;

    .complete-content {
      width: 320px;
      max-width: 90%;
      text-align: center;

      .complete-icon {
        i {
          font-size: 54px;
          color: #67C23A;
        }
      }

      .complete-title {
        margin-top: 10px;
        font-size: 16px;
        font-weight: bold;
        color: #303133;
      }

      .complete-actions {
        margin-top: 12px;

        .result-action {
          position: relative;
          display: inline-block;
          padding-left: 10px;

          .result-action-back {
            position: absolute;
            left: 0;
            top: 50%;
            transform: translate(-100%, -50%);
          }

          .el-link {
            font-size: 14px;
            padding: 7px 15px;
            border: 1px solid;
            border-radius: 4px;
            display: inline-flex;
            align-items: center;

            i {
              margin-right: 5px;
              font-size: 16px;
            }
          }
        }

        .result-close {
          .el-button {
            min-width: 90px;
          }
        }
      }
    }
  }
}
</style>
